/**
 * 官媒舱 hub(R2-T3,照 _design-mock/dept-reporter.html):
 * 三道门横幅 + 5 体裁场景卡墙(+ 发布中心 soon)+ 在途最近(统一作品线,列表不另建页)。
 *
 * hub 只回答「能做什么」:检索/筛选在 /works(IA 收口 F1-a),这里只留最近一条带。
 */
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Newspaper, PenLine, ShieldCheck } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { PageHeader } from "@/shared/ui/PageHeader";
import { useStaggerReveal } from "@/shared/motion/useStaggerReveal";
import { RecentWorksRow } from "@/entities/work/ui/RecentWorksRow";
import { DEPT_TITLE_KEY } from "@/entities/work/model/deptTitles";
import { REPORTER_CARDS } from "@/entities/scene-card/catalog";
import { REPORTER_WHOLE_ARTICLE_CREDITS_APPROX } from "@/entities/scene-card/model/creditRates";
import type { SceneCard } from "@/entities/scene-card/model/types";
import { SceneCapCard } from "@/entities/scene-card/ui/SceneCapCard";
import { SceneHubHero } from "@/entities/scene-card/ui/SceneHubHero";
import { SiteColumnsSection } from "@/features/reporter/ui/SiteColumnsSection";

function SectionHead({ title, sub, right }: { title: string; sub: string; right?: string }) {
  return (
    <div className="mb-3 flex flex-wrap items-baseline gap-2">
      <span className="h-3.5 w-[3px] self-center rounded-sm bg-primary" />
      <h2 className="text-sm font-semibold">{title}</h2>
      <span className="text-xs text-muted-foreground">{sub}</span>
      {right && <span className="ml-auto text-xs text-muted-foreground">{right}</span>}
    </div>
  );
}

export default function ReporterHubPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const cardsRef = useRef<HTMLDivElement>(null);
  useStaggerReveal(cardsRef);

  const open = (card: SceneCard) => {
    if (!card.route) return;
    navigate(card.route, card.preset ? { state: { scenePreset: card.preset } } : undefined);
  };
  // hero 概览只数真能点的卡(soon 不算「场景」,与「概览不许比卡墙乐观」同口径)
  const liveCards = REPORTER_CARDS.filter((c) => c.enabled);

  return (
    <div className="w-full space-y-6">
      <PageHeader
        icon={Newspaper}
        title={t(DEPT_TITLE_KEY.jizhe)}
        subtitle="企业软文一键成稿,带事实守卫 —— 不编时间、不编数据、不吹没有的事。"
        actions={
          <Button onClick={() => navigate("/ai-jizhe/create")}>
            <PenLine className="mr-2 h-4 w-4" />
            开始写作
          </Button>
        }
      />

      {/* 三道门横幅:事实边界守卫 → 四维质量评分 → 导出预检(R1 后端真闸,不是营销话术) */}
      <div className="flex items-start gap-3 rounded-[calc(var(--radius)+4px)] border border-success/40 bg-success/5 px-4 py-3 text-xs leading-relaxed">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-success" />
        <div>
          <b>每篇稿子都过三道门:</b>
          事实边界守卫(素材里没有的时间 / 地点 / 数据一律不写)→ 四维质量评分(低于门槛不放行)→
          导出预检。写得快,更写得<b>敢发</b>。
        </div>
      </div>

      <SceneHubHero hub="reporter" cards={liveCards} />

      <section className="scene-wall">
        <SectionHead
          title="选个体裁开写"
          sub="粘一段素材进来,AI 先整理再动笔"
          right={
            // ★数字必须来自 creditRates 单源 —— 这里原来手抄了一份「15」,
            //   0826 计费改 ×1.6 后常量该批没能重标,而这份手抄件连重标都覆盖不到
            `6 种体裁 · ${REPORTER_WHOLE_ARTICLE_CREDITS_APPROX} 积分/篇`
          }
        />
        <div ref={cardsRef} className="scene-grid">
          {REPORTER_CARDS.map((c, i) => (
            <SceneCapCard
              key={c.key}
              card={c}
              onOpen={c.enabled ? () => open(c) : undefined}
              index={i}
            />
          ))}
        </div>
      </section>

      {/* 1009 二期:官网栏目(定期发到建站舱官网新闻栏) */}
      <SiteColumnsSection />

      <RecentWorksRow
        lines={["jizhe"]}
        onOpen={(w) => navigate(w.detailRoute)}
        onViewAll={(href) => navigate(href)}
      />
    </div>
  );
}
